/**
 * The payroll report panel's pure text helpers, apart from the component so
 * they can be tested without the API client (type imports only).
 */
import type { TravelPayrollEntry, TravelPayrollEmailStatus, TravelPayrollEmailRun } from '@/lib/apiClient';

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * The Eastern calendar date at `now`, as [year, month (1-12), day].
 *
 * ⚠️ EASTERN, NOT THE BROWSER'S ZONE. The server's pay week and the weekly
 * email are Eastern (handbook item 8's Sunday–Saturday). A browser in another
 * zone near midnight would otherwise land on a different day — at 02:00 UTC
 * on a Sunday it is still Saturday in Eastern — and "Last pay week" would
 * pick, and a re-send would email, the week still in progress. (Codex, #558.)
 */
function easternYmd(now: Date): [number, number, number] {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return [get('year'), get('month'), get('day')];
}

/** The Sunday–Saturday before the week `now` falls in, in Eastern time. */
export function lastPayWeek(now: Date = new Date()): { from: string; to: string } {
  const [y, m, d] = easternYmd(now);
  // Plain calendar arithmetic on a UTC date: no zone can shift it again.
  const today = new Date(Date.UTC(y, m - 1, d));
  const sunday = new Date(Date.UTC(y, m - 1, d - today.getUTCDay() - 7));
  const saturday = new Date(Date.UTC(sunday.getUTCFullYear(), sunday.getUTCMonth(), sunday.getUTCDate() + 6));
  const ymd = (x: Date) => `${x.getUTCFullYear()}-${pad(x.getUTCMonth() + 1)}-${pad(x.getUTCDate())}`;
  return { from: ymd(sunday), to: ymd(saturday) };
}

const UNIT: Record<TravelPayrollEntry['lines'][number]['unit'], string> = {
  once: 'one time',
  'per-day': '/ day',
  'per-hour': '/ hour',
};

/** "Lodging $80.00 / day · Increased pay rate $1.50 / hour", or a plain no. */
export function rateSummary(lines: TravelPayrollEntry['lines']): string {
  if (!lines.length) return 'Nothing payable under the travel policy';
  return lines.map((l) => `${l.label} $${l.rate.toFixed(2)} ${UNIT[l.unit] ?? l.unit}`).join(' · ');
}

/** The one sentence that says whether the email is actually running. */
export function emailSummary(s: TravelPayrollEmailStatus): string {
  if (!s.enabled) {
    return 'The weekly payroll email is off. It is switched on in the app settings once the sending '
      + 'mailbox and its permission are in place.';
  }
  if (!s.from || !s.to.length) {
    return 'The weekly payroll email is switched on but has no sending mailbox or no recipient, so '
      + 'every send will fail until both are set.';
  }
  return `Last pay week's report is emailed to ${s.to.join(', ')} from ${s.from} on Monday morning, `
    + 'and retried each morning through Saturday if a send fails.';
}

/**
 * One line of the recent-sends list.
 *
 * ⚠️ "NOT CONFIRMED" IS ITS OWN STATE, not "sending" and not "failed". The
 * Project Tracker could not record how that send ended, and does not guess:
 * retrying might email payroll the same week twice. So it says what to check
 * and leaves the re-send to a person. (Codex, #558.)
 */
export function runLabel(r: TravelPayrollEmailRun): { text: string; tone: 'ok' | 'warn' | 'bad' } {
  const who = r.trigger_kind === 'manual' ? `re-sent${r.run_by_name ? ` by ${r.run_by_name}` : ''}` : 'scheduled';
  const head = `${r.week_from} to ${r.week_to} · ${who}`;
  if (r.unconfirmed) {
    const why = r.error ? ` (${r.error})` : '';
    return {
      text: `${head} · not confirmed — check the sending mailbox's Sent Items, and re-send if it did not go${why}`,
      tone: 'warn',
    };
  }
  const count = r.acknowledgments != null ? ` · ${r.acknowledgments} signed` : '';
  const err = r.error ? ` · ${r.error}` : '';
  return { text: `${head} · ${r.status}${count}${err}`, tone: r.status === 'failed' ? 'bad' : 'ok' };
}

